/*
  fmb-custom.js: global behaviours for Funky Monkey Bars (Impulse 9.2.0).
  Loaded with defer, only by snippets/fmb-head.liquid.
  Inventory: FMB-THEME-CUSTOMISATIONS.md.

  Conventions (CLAUDE.md section 5):
  - One IIFE with 'use strict'.
  - Shared helpers and constants at the top, above the init*() calls.
    Constants declared below the call that reads them are undefined at load.
  - Each feature is an initFeature() that bails early if its DOM is absent,
    so the file is safe on every page.
  - Config, strings and per-product data arrive as
    <script type="application/json"> blocks emitted by fmb-head.liquid.
    Never inline Liquid output into JS.
  - Where the theme re-renders markup after AJAX, particularly the cart
    drawer, re-apply from a MutationObserver that skips nodes we inserted.

  Debugging: add the class fmb-debug to the html element to see log output.
*/
(function () {
  'use strict';

  /* --- Constants --- */

  var CONFIG_ID = 'fmb-config';
  var FMB_VERSION = '0.1.0';

  // Spec panels (snippets/fmb-spec-panels.liquid).
  var SPEC_ACCESSORIES = '.fmb-spec--accessories';
  var SPEC_SEARCH = '.fmb-spec__search';
  var SPEC_ACCESSORY = '.fmb-spec__accessory';
  var SPEC_TRACK = '.fmb-spec__track';
  var SPEC_SCROLLER_CLASS = 'fmb-spec__scroller';
  var SPEC_ARROW_CLASS = 'fmb-spec__arrow';
  var SPEC_DISABLED_CLASS = 'is-disabled';
  // Arrow step, as the live 4.1.4 monkey-bars-product.js: most of the
  // visible track width, never less than 260px.
  var SPEC_SCROLL_MIN = 260;
  var SPEC_SCROLL_RATIO = 0.78;
  var SPEC_ARROWS = [
    { modifier: 'prev', label: 'Scroll left', direction: -1 },
    { modifier: 'next', label: 'Scroll right', direction: 1 }
  ];

  // Custom Product Builder (Buildateam) cart lines. The app adds the
  // configured frame as a parent line at $0.00 ("Variant for price 0") beside
  // the priced component lines. Rows are stock snippets/cart-item.liquid,
  // div.cart__item[data-key], inside [data-products] on the cart page
  // (sections/main-cart.liquid) and in the drawer (snippets/cart-drawer.liquid).
  var CPB_CART_DATA_ID = 'fmb-cpb-cart-lines';
  var CPB_CART_STYLE_ID = 'fmb-cpb-cart-lines-style';
  var CPB_PARENT_CLASS = 'fmb-cpb-parent-line';
  var CPB_TYPE_PREFIX = 'cpb_';
  var CART_PRODUCTS = '[data-products]';
  var CART_ROW = '.cart__item[data-key]';
  var CPB_CART_REFRESH_MS = 150;
  // The parent line's variant title, shown by stock cart-item.liquid in
  // .cart__item--variants. Only a cheap hint that a re-rendered cart (the
  // drawer after an AJAX add) may hold a parent line, so /cart.js is called
  // only then; /cart.js always decides what is removed.
  var CPB_VARIANT_TITLE = 'Variant for price 0';

  // Cart actions (sections/fmb-cart-actions.liquid): the Empty cart button.
  var CART_CLEAR_BUTTON = '[data-fmb-cart-clear]';
  var CART_CLEAR_URL = '/cart/clear.js';
  var CART_CLEAR_FALLBACK_URL = '/cart/clear';

  // Content links in a new tab (rich text PDF lists on the assembly
  // instructions page). Stock rich text renders in .rte, and text columns
  // block text (sections/text-columns.liquid) in .rte-setting.
  var CONTENT_LINKS = '.rte a[href], .rte-setting a[href]';
  var CONTENT_PDF_PATH = /\.pdf$/i;
  var CONTENT_NEW_TAB_HOSTS = ['vimeo.com', 'www.vimeo.com'];

  /* --- Shared helpers --- */

  function qs(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function qsa(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  // Parses a <script type="application/json"> block by id. Returns null when
  // the block is missing or its content is not valid JSON.
  function readJson(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      log('readJson: invalid JSON in #' + id, err);
      return null;
    }
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  // Returns a function that runs fn at most once per animation frame,
  // however often it is called (scroll and resize handlers).
  function rafThrottle(fn) {
    var queued = false;
    return function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        fn();
      });
    };
  }

  // Logs only when the html element carries the class fmb-debug.
  function log() {
    if (!document.documentElement.classList.contains('fmb-debug')) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[fmb]');
    console.log.apply(console, args);
  }

  /* --- Module state --- */

  var config = null;

  /* --- Features --- */

  // Reads the fmb-config JSON emitted by fmb-head.liquid and exposes it on
  // window.FMB for other fmb scripts and for debugging.
  function initConfig() {
    var data = readJson(CONFIG_ID);
    if (!data) {
      log('initConfig: #' + CONFIG_ID + ' missing or invalid; is the header hook placed?');
      return;
    }
    config = data;
    window.FMB = { config: config, version: FMB_VERSION };
    log('initConfig: loaded', config);
  }

  // Confirms the footer hook section rendered fmb-body-end.
  function initHookCheck() {
    if (!qs('[data-fmb-body-end]')) {
      log('initHookCheck: [data-fmb-body-end] not found; the footer hook section is not placed');
      return;
    }
    log('initHookCheck: both hooks rendered');
  }

  // Disables a spec arrow and adds is-disabled, which the CSS hides.
  function setSpecArrowDisabled(button, disabled) {
    button.disabled = disabled;
    button.classList.toggle(SPEC_DISABLED_CLASS, disabled);
  }

  // Wraps a spec track in div.fmb-spec__scroller and builds its previous and
  // next buttons. Returns a refresh function that inserts the buttons while
  // the track overflows, removes them when it does not, and disables the
  // button at whichever end the track is scrolled to.
  function setupSpecTrack(track) {
    var scroller = document.createElement('div');
    scroller.className = SPEC_SCROLLER_CLASS;
    track.parentNode.insertBefore(scroller, track);
    scroller.appendChild(track);

    var buttons = SPEC_ARROWS.map(function (arrow) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = SPEC_ARROW_CLASS + ' ' + SPEC_ARROW_CLASS + '--' + arrow.modifier;
      button.setAttribute('aria-label', arrow.label);
      button.addEventListener('click', function () {
        var step = Math.max(SPEC_SCROLL_MIN, track.clientWidth * SPEC_SCROLL_RATIO);
        track.scrollBy({ left: arrow.direction * step, behavior: 'smooth' });
      });
      return button;
    });
    var prev = buttons[0];
    var next = buttons[1];

    function refresh() {
      if (track.scrollWidth <= track.clientWidth) {
        buttons.forEach(function (button) {
          if (button.parentNode) button.parentNode.removeChild(button);
        });
        return;
      }
      if (!prev.parentNode) {
        scroller.insertBefore(prev, track);
        scroller.appendChild(next);
      }
      setSpecArrowDisabled(prev, track.scrollLeft <= 0);
      setSpecArrowDisabled(next, track.scrollLeft + track.clientWidth >= track.scrollWidth - 1);
    }

    track.addEventListener('scroll', rafThrottle(refresh), { passive: true });
    refresh();
    return refresh;
  }

  // Filters the compatible accessories cards in one spec accessories panel
  // as the customer types. A card is hidden when its text does not contain
  // the query. onFilter runs after each pass, as the track width changes.
  function initAccessorySearch(panel, onFilter) {
    var input = qs(SPEC_SEARCH, panel);
    if (!input) return;
    var cards = qsa(SPEC_ACCESSORY, panel);
    input.addEventListener('input', function () {
      var query = input.value.trim().toLowerCase();
      cards.forEach(function (card) {
        card.hidden = card.textContent.toLowerCase().indexOf(query) === -1;
      });
      onFilter();
    });
  }

  // Spec panel tracks (accessories and gallery) get previous and next arrows,
  // re-evaluated on resize, and the accessories panel gets its search filter.
  // Markup: snippets/fmb-spec-panels.liquid, panels 'accessories' and
  // 'gallery'. Styles: assets/fmb-spec-panels.css.
  function initSpecPanels() {
    var tracks = qsa(SPEC_TRACK);
    if (!tracks.length) return;
    var refreshers = tracks.map(setupSpecTrack);
    var refreshAll = rafThrottle(function () {
      refreshers.forEach(function (refresh) {
        refresh();
      });
    });
    window.addEventListener('resize', refreshAll);
    window.addEventListener('load', refreshAll);
    qsa(SPEC_ACCESSORIES).forEach(function (panel) {
      initAccessorySearch(panel, refreshAll);
    });
    log('initSpecPanels: ' + tracks.length + ' track(s) with arrows, accessories search ready');
  }

  // True only for a builder parent line: product type starting cpb_ and a
  // unit price of exactly 0 both before and after discounts. A paid line, or
  // a line discounted to 0 that was not 0 to begin with, never matches.
  function isCpbParentLine(item) {
    var type = String(item.product_type || '');
    return type.indexOf(CPB_TYPE_PREFIX) === 0 &&
      Number(item.original_price) === 0 &&
      Number(item.final_price) === 0;
  }

  // Hides the rows whose line item key is in keys and shows again any row we
  // hid earlier that is no longer in keys. Rows we never marked are left
  // alone. The row stays in the DOM (hidden attribute plus a class) so the
  // stock cart JS and the cart form's updates[] inputs still find it.
  function applyCpbCartLines(keys) {
    qsa(CART_ROW).forEach(function (row) {
      var hide = keys.indexOf(row.getAttribute('data-key')) !== -1;
      if (hide) {
        row.hidden = true;
        row.classList.add(CPB_PARENT_CLASS);
      } else if (row.classList.contains(CPB_PARENT_CLASS)) {
        row.hidden = false;
        row.classList.remove(CPB_PARENT_CLASS);
      }
    });
  }

  // Removes the builder's zero-price parent line from the cart, so it never
  // reaches checkout, where it showed as a FREE item with the long list of
  // configuration properties. The live 4.1.4 cart.liquid did the same: it
  // posted quantity 0 to /cart/change for each such line on cart page load.
  // Trade-off: the removed line carried the configuration summary and the
  // render image, so the order holds only the priced component lines, as it
  // did on 4.1.4.
  //
  // Flow: the row is hidden from first paint (the keys fmb-head.liquid emits
  // in #fmb-cpb-cart-lines and its matching inline style, removed here once
  // the class has taken over). /cart.js then confirms which lines are parent
  // lines (isCpbParentLine(): cpb_ type, 0 before and after discounts), and
  // each one is removed with POST /cart/change.js quantity 0, one at a time,
  // since Shopify rejects concurrent cart changes. A key is posted at most
  // once per page view. After at least one removal, the stock cart:build
  // event makes every theme.CartForm (cart page and drawer) re-render its
  // rows, subtotal, discounts and the header count from /cart?view=ajax, and
  // cart:updated is sent with the final cart as the stock quantity change
  // does. On a network error the chain stops and the row stays hidden.
  //
  // No request is made unless a parent line is expected: the pre-rendered
  // keys are non-empty, or a re-rendered row shows CPB_VARIANT_TITLE.
  // Styles: fmb-custom.css block "Cart: remove the builder's zero-price
  // parent line".
  function initCpbCartLines() {
    var containers = qsa(CART_PRODUCTS);
    if (!containers.length) return;

    var keys = readJson(CPB_CART_DATA_ID);
    if (!Array.isArray(keys)) keys = [];
    applyCpbCartLines(keys);
    var style = document.getElementById(CPB_CART_STYLE_ID);
    if (style) style.parentNode.removeChild(style);

    var themeRoutes = (window.theme && window.theme.routes) || {};
    var configRoutes = (config && config.routes) || {};
    var cartUrl = themeRoutes.cart || ((configRoutes.cart || '/cart') + '.js');
    var changeUrl = themeRoutes.cartChange || ((configRoutes.cartChange || '/cart/change') + '.js');
    var attempted = {};
    var timer = null;
    var requestId = 0;
    var removing = false;

    function hasCandidate() {
      if (keys.length) return true;
      return qsa(CART_ROW).some(function (row) {
        return row.textContent.indexOf(CPB_VARIANT_TITLE) !== -1;
      });
    }

    // Hides, provisionally, rows re-rendered with CPB_VARIANT_TITLE (the
    // drawer after an AJAX add, when the pre-rendered keys are stale), so the
    // parent line does not show while /cart.js is asked. refresh() then
    // calls applyCpbCartLines(), which shows any such row again unless
    // /cart.js confirms it is a parent line.
    function hideCandidates() {
      qsa(CART_ROW).forEach(function (row) {
        if (row.textContent.indexOf(CPB_VARIANT_TITLE) === -1) return;
        row.hidden = true;
        row.classList.add(CPB_PARENT_CLASS);
      });
    }

    function getJson(response) {
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return response.json();
    }

    // Posts quantity 0 for each key in turn. Resolves with the cart from the
    // last successful change, or null if nothing was removed. The first
    // failure stops the chain; keys after it are left for the next view.
    function removeLines(toRemove) {
      var lastCart = null;
      return toRemove.reduce(function (chain, key) {
        return chain.then(function () {
          attempted[key] = true;
          return fetch(changeUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
              Accept: 'application/json'
            },
            body: JSON.stringify({ id: key, quantity: 0 })
          }).then(getJson).then(function (cart) {
            lastCart = cart;
          });
        });
      }, Promise.resolve()).then(function () {
        return lastCart;
      }, function (err) {
        log('initCpbCartLines: /cart/change.js failed, row left hidden', err);
        return lastCart;
      });
    }

    function refresh() {
      if (removing) return;
      var id = ++requestId;
      fetch(cartUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(getJson)
        .then(function (cart) {
          if (id !== requestId) return;
          keys = (cart.items || []).filter(isCpbParentLine).map(function (item) {
            return item.key;
          });
          applyCpbCartLines(keys);
          var toRemove = keys.filter(function (key) {
            return !attempted[key];
          });
          if (!toRemove.length) return;
          removing = true;
          return removeLines(toRemove).then(function (lastCart) {
            removing = false;
            if (!lastCart) return;
            keys = (lastCart.items || []).filter(isCpbParentLine).map(function (item) {
              return item.key;
            });
            var removed = toRemove.filter(function (key) {
              return keys.indexOf(key) === -1;
            });
            log('initCpbCartLines: removed ' + removed.length + ' parent line(s)');
            document.dispatchEvent(new CustomEvent('cart:build'));
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: lastCart } }));
          });
        })
        .catch(function (err) {
          removing = false;
          log('initCpbCartLines: /cart.js failed, keeping the last known keys', err);
        });
    }

    // Only childList changes trigger this; our own changes are attributes
    // and classes, so the observer never reacts to itself. The last known
    // keys are re-applied at once so the line does not flash, then /cart.js
    // is asked only if a parent line may be present.
    var observer = new MutationObserver(function (mutations) {
      var added = mutations.some(function (m) {
        return m.addedNodes.length > 0;
      });
      if (!added) return;
      applyCpbCartLines(keys);
      if (!hasCandidate()) return;
      hideCandidates();
      window.clearTimeout(timer);
      timer = window.setTimeout(refresh, CPB_CART_REFRESH_MS);
    });
    containers.forEach(function (el) {
      observer.observe(el, { childList: true, subtree: true });
    });
    if (keys.length) refresh();
    log('initCpbCartLines: watching ' + containers.length + ' cart container(s), ' + keys.length + ' parent line(s) expected');
  }

  // Empty cart button in the FMB cart actions section on the cart page,
  // as on the 4.1.4 cart page. Runs only on a click, never on load. POSTs
  // /cart/clear.js (theme.routes.cartClear when the theme defines one, else
  // the button's data-clear-url from routes.cart_clear_url, else the plain
  // path), with the button disabled and showing data-working-label while the
  // request is in flight.
  //
  // On success, cart:updated is sent with the empty cart, as the stock
  // quantity change does, and cart:build makes every theme.CartForm (the
  // drawer, if enabled) re-render and the header count drop to 0. The page
  // then reloads: stock main-cart.liquid renders its empty state ("Your cart
  // is currently empty") server side only, and this section renders nothing
  // for an empty cart, so a re-render alone would leave an empty form with a
  // $0 subtotal and a checkout button. Without theme.CartForm the page just
  // reloads. On failure the button is restored and the browser goes to
  // /cart/clear (data-fallback-url), which clears the cart and redirects to
  // the cart page.
  function initCartActions() {
    var buttons = qsa(CART_CLEAR_BUTTON);
    if (!buttons.length) return;

    var themeRoutes = (window.theme && window.theme.routes) || {};

    buttons.forEach(function (button) {
      var label = button.textContent;
      var working = button.getAttribute('data-working-label') || label;
      var clearUrl = themeRoutes.cartClear || button.getAttribute('data-clear-url') || CART_CLEAR_URL;
      var fallbackUrl = button.getAttribute('data-fallback-url') || CART_CLEAR_FALLBACK_URL;

      button.addEventListener('click', function (evt) {
        evt.preventDefault();
        if (button.disabled) return;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = working;

        fetch(clearUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json'
          }
        })
          .then(function (response) {
            if (!response.ok) throw new Error('HTTP ' + response.status);
            return response.json();
          })
          .then(function (cart) {
            log('initCartActions: cart cleared');
            if (window.theme && window.theme.CartForm) {
              document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
              document.dispatchEvent(new CustomEvent('cart:build'));
            }
            window.location.reload();
          })
          .catch(function (err) {
            log('initCartActions: /cart/clear.js failed, going to ' + fallbackUrl, err);
            button.disabled = false;
            button.removeAttribute('aria-busy');
            button.textContent = label;
            window.location.href = fallbackUrl;
          });
      });
    });
    log('initCartActions: ' + buttons.length + ' Empty cart button(s) ready');
  }

  /* --- Content links: PDFs and Vimeo open in a new tab (trigger: the rich text PDF lists on the assembly instructions page, templates/page.assembly-instructions.json text columns blocks) --- */
  // Shopify's rich text setting cannot store target or rel, so links the
  // client adds with the editor's link tool open in the same tab. Any link
  // inside rich text whose path ends in .pdf (case-insensitive, query
  // ignored) or whose host is vimeo.com or www.vimeo.com gets
  // target="_blank" and rel="noopener". Links that already have a target
  // are left alone. Runs once on load; no Liquid, no strings.
  function initContentLinksNewTab() {
    var links = qsa(CONTENT_LINKS);
    if (!links.length) return;

    var count = 0;
    links.forEach(function (link) {
      if (link.hasAttribute('target')) return;
      var url;
      try {
        url = new URL(link.getAttribute('href'), window.location.href);
      } catch (err) {
        return;
      }
      var host = url.hostname.toLowerCase();
      var isPdf = CONTENT_PDF_PATH.test(url.pathname);
      var isVimeo = CONTENT_NEW_TAB_HOSTS.indexOf(host) !== -1;
      if (!isPdf && !isVimeo) return;
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener');
      count += 1;
    });
    log('initContentLinksNewTab: ' + count + ' link(s) set to open in a new tab');
  }

  /*
    Pattern for future features (not implemented yet):

      function initFeature() {
        var root = qs('[data-fmb-feature]');
        if (!root) return;            // bail early, the file runs on every page
        var data = readJson('fmb-feature-data');
        if (!data) return;
        // ...
      }

    Cart drawer rule: theme.CartForm.buildCart() (theme.js line 1534 in 10.0.0) fetches
    the cart page with ?view=ajax and replaces the HTML inside [data-products],
    discarding anything we added. Re-apply from a MutationObserver on
    [data-products] (childList, subtree), and skip mutations whose added nodes
    carry our own marker (for example data-fmb-inserted) so the observer does
    not loop on its own output:

      var observer = new MutationObserver(function (mutations) {
        var ours = mutations.every(function (m) {
          return Array.prototype.every.call(m.addedNodes, function (n) {
            return n.nodeType === 1 && n.hasAttribute('data-fmb-inserted');
          });
        });
        if (!ours) applyFeature();
      });
      qsa('[data-products]').forEach(function (el) {
        observer.observe(el, { childList: true, subtree: true });
      });
  */

  /* --- Init --- */

  onReady(function () {
    initConfig();
    initHookCheck();
    initSpecPanels();
    initCpbCartLines();
    initCartActions();
    initContentLinksNewTab();
  });
})();
